import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatDateSr } from "@normax-performance/shared-format";
import type { ReportPreviewRequestInput, ReportType } from "@normax-performance/shared-types";
import { analyticsApi, catalogApi, reportsApi, workersApi } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import { Button } from "../../shared-ui/primitives";
import { REPORT_TYPE_REGISTRY, getReportTypeMeta } from "./reportTypeRegistry";
import { computeReportPeriod, type ResolvableReportPeriodPresetKey } from "./period";
import { ReportSummaryRow } from "./components/ReportSummaryRow";
import { ReportInsightsList } from "./components/ReportInsightsList";
import { ReportSectionTable } from "./components/ReportSectionTable";
import { ReportZoneDistribution } from "./components/ReportZoneDistribution";
import { ReportCompletenessFooter } from "./components/ReportCompletenessFooter";
import { ReportEntityPicker } from "./components/ReportEntityPicker";
import { ReportPeriodControl } from "./components/ReportPeriodControl";
import "./ReportDetailPage.css";

const VALID_TYPES = new Set(REPORT_TYPE_REGISTRY.map((r) => r.reportType));

function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
}

// `titleKey` arrives as plain `string` on both `ReportTypeMeta` (Task 12)
// and the backend's `ReportSection` DTO (data-driven keys, not compile-time
// literal unions), so `t()` can't type-check them against the generated key
// union — same fix `ReportsPage.tsx`/`ReportSummaryRow.tsx`/
// `ReportSectionTable.tsx` already apply: cast `as never` and supply the raw
// key as `defaultValue` so a genuinely missing key still renders something
// instead of throwing.
function translateDynamic(t: TFunction<"reports">, key: string, fallback: string): string {
  return t(key as never, { defaultValue: fallback });
}

/** URL-encoded report state (task spec §10): `type`, entity id, `from`, `to`
 * fully determine the view — bookmarkable, shareable, reload-safe. Invalid
 * or incomplete state renders a clean fallback (picker, error, or loading
 * shell), never a crash. */
export function ReportDetailPage() {
  const { t } = useTranslation("reports");
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get("type");
  const isValidType = typeParam !== null && VALID_TYPES.has(typeParam as ReportType);
  const reportType = isValidType ? (typeParam as ReportType) : null;
  const meta = reportType ? getReportTypeMeta(reportType) : null;

  const workerId = searchParams.get("workerId");
  const productId = searchParams.get("productId");
  const operationId = searchParams.get("operationId");
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");

  const hasRequiredEntity =
    !meta?.requiresEntity ||
    (meta.requiresEntity === "worker" && !!workerId) ||
    (meta.requiresEntity === "product" && !!productId) ||
    (meta.requiresEntity === "operation" && !!operationId);

  const hasExplicitPeriod = !!fromParam && !!toParam;
  const periodIsValid = !hasExplicitPeriod || (isValidIsoDate(fromParam!) && isValidIsoDate(toParam!) && fromParam! <= toParam!);

  const latestDayQuery = useQuery({
    queryKey: ["analytics", "latest-reporting-day"],
    queryFn: () => analyticsApi.latestReportingDay(),
    enabled: isValidType && !hasExplicitPeriod && meta?.defaultPeriodPreset === "lastRecordedDay",
  });

  useEffect(() => {
    if (!isValidType || hasExplicitPeriod || !meta) return;
    if (meta.defaultPeriodPreset === "lastRecordedDay") {
      if (latestDayQuery.data?.date) {
        const next = new URLSearchParams(searchParams);
        next.set("from", latestDayQuery.data.date);
        next.set("to", latestDayQuery.data.date);
        setSearchParams(next, { replace: true });
      }
      return;
    }
    const range = computeReportPeriod(meta.defaultPeriodPreset as ResolvableReportPeriodPresetKey, new Date());
    const next = new URLSearchParams(searchParams);
    next.set("from", range.from);
    next.set("to", range.to);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidType, hasExplicitPeriod, meta, latestDayQuery.data]);

  const workersQuery = useQuery({ queryKey: ["workers", "list"], queryFn: () => workersApi.list(), enabled: meta?.requiresEntity === "worker" && !workerId });
  const productsQuery = useQuery({ queryKey: ["catalog", "products"], queryFn: () => catalogApi.products(), enabled: meta?.requiresEntity === "product" && !productId });
  const operationsQuery = useQuery({ queryKey: ["catalog", "operations"], queryFn: () => catalogApi.operations(), enabled: meta?.requiresEntity === "operation" && !operationId });

  const canFetchReport = isValidType && hasRequiredEntity && hasExplicitPeriod && periodIsValid;
  const reportQuery = useQuery({
    queryKey: ["reports", "preview", reportType, fromParam, toParam, workerId, productId, operationId],
    queryFn: () =>
      reportsApi.preview({
        reportType: reportType!,
        from: fromParam!,
        to: toParam!,
        ...(workerId ? { workerId } : {}),
        ...(productId ? { productId } : {}),
        ...(operationId ? { operationId } : {}),
      }),
    enabled: canFetchReport,
  });

  const [pdfState, setPdfState] = useState<"idle" | "loading" | "error">("idle");
  const [pdfErrorKey, setPdfErrorKey] = useState<"pdf.error" | "pdf.errorBusy">("pdf.error");

  async function downloadPdf(request: ReportPreviewRequestInput) {
    setPdfState("loading");
    setPdfErrorKey("pdf.error");
    try {
      const { blob, filename } = await reportsApi.downloadPdf(request);
      const url = URL.createObjectURL(blob);
      // `document` is shadowed by the local `ReportDocument` binding below —
      // reach the DOM through `window` explicitly.
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = filename ?? "NormaX_Izvestaj.pdf";
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoke on the next tick so the download has certainly started.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setPdfState("idle");
    } catch (error) {
      if (error instanceof ApiError && error.code === "REPORT_PDF_BUSY") {
        setPdfErrorKey("pdf.errorBusy");
      }
      setPdfState("error");
    }
  }

  function updateEntity(kind: "worker" | "product" | "operation", id: string) {
    const next = new URLSearchParams(searchParams);
    next.set(`${kind}Id`, id);
    setSearchParams(next);
  }

  function updatePeriod(range: { from: string; to: string }) {
    const next = new URLSearchParams(searchParams);
    next.set("from", range.from);
    next.set("to", range.to);
    setSearchParams(next);
  }

  if (!isValidType || (hasExplicitPeriod && !periodIsValid)) {
    return (
      <div className="nx-report-detail__state" role="alert">
        <h1>{t("states.invalidUrlTitle")}</h1>
        <p>{t("states.invalidUrlBody")}</p>
        <Link to="/izvestaji">{t("states.backToReports")}</Link>
      </div>
    );
  }

  if (meta!.requiresEntity && !hasRequiredEntity) {
    return (
      <div className="nx-report-detail">
        <h1>{translateDynamic(t, meta!.titleKey, meta!.titleKey)}</h1>
        <ReportEntityPicker
          kind={meta!.requiresEntity}
          workers={workersQuery.data ?? []}
          products={productsQuery.data ?? []}
          operations={operationsQuery.data ?? []}
          onSelect={(id) => updateEntity(meta!.requiresEntity!, id)}
        />
      </div>
    );
  }

  const isAwaitingLastRecordedDay = meta!.defaultPeriodPreset === "lastRecordedDay" && !hasExplicitPeriod;
  if (isAwaitingLastRecordedDay && latestDayQuery.isError) {
    // Without this branch, a failed `latestReportingDay` call (network/API
    // error) never sets `isSuccess`, so the generic loading branch below
    // would render forever with no retry affordance (same class of bug as
    // the null-date case above, triggered by a query error instead).
    return (
      <div className="nx-report-detail__state" role="alert">
        <h1>{translateDynamic(t, meta!.titleKey, meta!.titleKey)}</h1>
        <p>{t("states.errorTitle")}</p>
        <button type="button" onClick={() => latestDayQuery.refetch()}>
          {t("states.errorRetry")}
        </button>
      </div>
    );
  }
  if (isAwaitingLastRecordedDay && latestDayQuery.isSuccess && latestDayQuery.data.date === null) {
    // Honest empty state (task spec §7: "If no historical day exists: show
    // honest empty state") — without this branch, a tenant with zero
    // recorded history would sit in the generic loading shell forever,
    // since the freeze-into-URL effect above only fires when `date` is
    // non-null.
    return (
      <div className="nx-report-detail__state">
        <h1>{translateDynamic(t, meta!.titleKey, meta!.titleKey)}</h1>
        <p>{t("period.noRecordedDay")}</p>
      </div>
    );
  }

  if (!hasExplicitPeriod || reportQuery.isLoading) {
    return <div className="nx-report-detail__state">{t("states.loading")}</div>;
  }

  if (reportQuery.isError) {
    return (
      <div className="nx-report-detail__state" role="alert">
        <h1>{t("states.errorTitle")}</h1>
        <button type="button" onClick={() => reportQuery.refetch()}>
          {t("states.errorRetry")}
        </button>
      </div>
    );
  }

  const document = reportQuery.data!;
  const isEmpty = document.sections.every((s) => s.tables.every((table) => table.rows.length === 0)) && document.completeness.recordedWorkerCount === 0;
  const periodRecap = `${formatDateSr(document.context.period.from)} – ${formatDateSr(document.context.period.to)}`;

  const pdfRequest: ReportPreviewRequestInput = {
    reportType: reportType!,
    from: fromParam!,
    to: toParam!,
    ...(workerId ? { workerId } : {}),
    ...(productId ? { productId } : {}),
    ...(operationId ? { operationId } : {}),
  };

  return (
    <div className="nx-report-detail">
      <header className="nx-report-detail__header">
        {/* Level 1 — compact sticky identity (task:
            docs/NORMAX_PERFORMANCE_REPORT_DETAIL_STICKY_CONTEXT_FORENSIC_REPORT.md
            §10/§12): title/type + subject + period ONLY. Deliberately
            excludes the period-control and PDF action below — those used to
            share this same sticky box, making its real height variable
            (custom-date sub-row, wrapping PDF error) and unreliable against
            the fixed `--sticky-context-height` every report table's sticky
            `<thead>` is offset by. This box's content is now four short,
            non-interactive text lines only — stable for a given report/
            viewport, so a static offset is finally trustworthy. */}
        <div className="nx-report-detail__heading nx-sticky-context">
          <p className="nx-report-detail__eyebrow">{t("landing.title")}</p>
          <h1 className="nx-report-detail__title">{translateDynamic(t, meta!.titleKey, meta!.titleKey)}</h1>
          {document.context.subject ? (
            <p className="nx-report-detail__subject">
              {document.context.subject.code ? `${document.context.subject.code} — ` : ""}
              {document.context.subject.name}
            </p>
          ) : null}
          <p className="nx-report-detail__period">{periodRecap}</p>
        </div>

        {/* Non-sticky toolbar — period preset/custom-date controls and the
            PDF action scroll away normally once the user starts reading a
            table; unchanged behavior, only relocated out of the sticky
            layer (forensic report §10). */}
        <div className="nx-report-detail__actions">
          <ReportPeriodControl
            period={{ from: fromParam!, to: toParam! }}
            onChange={updatePeriod}
            singleDayOnly={meta!.defaultPeriodPreset === "lastRecordedDay"}
            onLastRecordedDay={
              meta!.defaultPeriodPreset === "lastRecordedDay"
                ? () => {
                    const next = new URLSearchParams(searchParams);
                    next.delete("from");
                    next.delete("to");
                    setSearchParams(next);
                  }
                : undefined
            }
          />
          {!isEmpty ? (
            <div className="nx-report-detail__pdf">
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={pdfState === "loading"}
                onClick={() => void downloadPdf(pdfRequest)}
              >
                {pdfState === "loading" ? t("pdf.generating") : t("pdf.download")}
              </Button>
              {pdfState === "error" ? (
                <span className="nx-report-detail__pdf-error" role="alert">
                  {t(pdfErrorKey)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {isEmpty ? (
        <div className="nx-report-detail__state">
          <h2>{t("states.emptyTitle")}</h2>
          <p>{t("states.emptyBody", { reportType: translateDynamic(t, meta!.titleKey, meta!.titleKey), period: periodRecap })}</p>
        </div>
      ) : (
        <div className="nx-report-document">
          <ReportSummaryRow metrics={document.summary.metrics} timeDistribution={document.summary.timeDistribution} />
          <ReportInsightsList insights={document.insights} />
          {document.sections.map((section) => {
            const sectionIsEmpty = section.tables.every((table) => table.rows.length === 0);
            // Never a bare heading over nothing (task spec §8): the one
            // section worth an explicit "nothing to report" line is
            // "attention" (a calm, honest confirmation reads as reassuring
            // information); every other genuinely-empty section is simply
            // omitted rather than showing a heading with no content under it.
            if (sectionIsEmpty && section.id !== "attention") return null;
            return (
              <section key={section.id} className="nx-report-detail__section" aria-labelledby={`nx-report-section-${section.id}`}>
                <h2 id={`nx-report-section-${section.id}`} className="nx-report-detail__section-title">
                  {translateDynamic(t, section.titleKey, section.titleKey)}
                </h2>
                {sectionIsEmpty ? (
                  <p className="nx-report-detail__section-empty">{t("states.attentionEmpty")}</p>
                ) : (
                  section.tables.map((table) =>
                    table.id === "zoneDistribution" ? (
                      <ReportZoneDistribution key={table.id} rows={table.rows as { zone: string; count: number }[]} showTitle={section.tables.length > 1} />
                    ) : (
                      <ReportSectionTable key={table.id} table={table} showTitle={section.tables.length > 1} />
                    ),
                  )
                )}
              </section>
            );
          })}
          <ReportCompletenessFooter completeness={document.completeness} />
        </div>
      )}
    </div>
  );
}
